/**
 * hooks/useScannerBridge.ts
 *
 * Reads scanner picks written by MER_HighConviction_Scanner into
 * localStorage['mer_scanner_picks'] and auto-adds them to the watchlist.
 *
 * Also exposes scanner context (score, catalyst, setup_type) so the
 * ThesisPanel and promptBuilder can enrich AI prompts with it.
 *
 * Zero regression — pure addition. Writes nothing to existing store keys.
 * Reads only 'mer_scanner_picks'. Adds symbols via the existing addSymbol action.
 */

import { useEffect, useCallback } from 'react';
import { useStore } from '../../store';
import { emitAlert } from '../../features/alerts/alertStore';
import { logEvent } from '../../features/status/systemEventLog';

// ─── Bridge storage key (must match scanner) ──────────────────────────────────
export const BRIDGE_KEY = 'mer_scanner_picks';

// ─── Scanner result shape (as written by the scanner) ────────────────────────
export interface ScannerPick {
  ticker:            string;
  company_name:      string;
  sector:            string;
  score:             number;   // 1.0–5.0
  rating:            string;   // STRONG BUY | BUY | ...
  conviction:        string;   // HIGH | MEDIUM | LOW
  setup_type:        string;
  key_catalyst:      string;
  price_target_bias: string;   // BULLISH | NEUTRAL | BEARISH
  universe:          string;   // QQQ | SPY
  scannedAt:         number;   // Unix ms
}

// ─── In-memory context store — per ticker ─────────────────────────────────────
// Used by promptBuilder to enrich AI thesis with scanner context
const contextStore = new Map<string, ScannerPick>();

export function getScannerContext(symbol: string): ScannerPick | null {
  return contextStore.get(symbol.toUpperCase()) || null;
}

export function getAllScannerContext(): Map<string, ScannerPick> {
  return contextStore;
}

// ─── Read from localStorage ───────────────────────────────────────────────────
// Resolve the server origin — matches how the app itself connects
function getServerOrigin(): string {
  if (typeof window !== 'undefined' && window.location.host) {
    return `${window.location.protocol}//${window.location.host}`;
  }
  return 'http://localhost:3000';
}

// Read picks from server API (primary) with localStorage fallback
async function readBridgePicks(): Promise<ScannerPick[]> {
  // Primary: fetch from server /api/bridge/picks (works cross-origin)
  try {
    const origin = getServerOrigin();
    const res = await fetch(`${origin}/api/bridge/picks`, { signal: AbortSignal.timeout(3000) });
    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data.picks) && data.picks.length > 0) {
        console.log(`[Bridge] ✓ ${data.picks.length} picks received from server`);
        return data.picks as ScannerPick[];
      }
    }
  } catch {
    // Server not available — fall through to localStorage
  }

  // Fallback: localStorage (only works when same origin)
  try {
    const raw = localStorage.getItem(BRIDGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ScannerPick[];
    if (!Array.isArray(parsed)) return [];
    const cutoff = Date.now() - 12 * 60 * 60 * 1000;
    const picks = parsed.filter(p => p.ticker && p.scannedAt > cutoff);
    if (picks.length) {
      localStorage.removeItem(BRIDGE_KEY); // clear after reading
      return picks;
    }
  } catch {}

  return [];
}

// ─── Hook ─────────────────────────────────────────────────────────────────────
export function useScannerBridge() {
  const { watchlist, addSymbol } = useStore();

  const importPicks = useCallback(async () => {
    const picks = await readBridgePicks();
    if (!picks.length) return 0;

    const existingSymbols = new Set(watchlist.map(w => w.symbol));
    let added = 0;

    for (const pick of picks) {
      const sym = pick.ticker.toUpperCase();

      // Store context regardless of whether we add to watchlist
      contextStore.set(sym, pick);

      // Only add to watchlist if not already present
      if (!existingSymbols.has(sym)) {
        addSymbol(sym);
        added++;
      }
    }

    if (added > 0) {
      emitAlert(
        null,
        'opportunity',
        'actionable',
        `Scanner Bridge: ${added} symbol${added > 1 ? 's' : ''} added`,
        `${added} high-conviction pick${added > 1 ? 's' : ''} from MER Scanner loaded into watchlist.`,
        'bridge:import'
      );
      logEvent('market', `Scanner bridge: ${added} symbols imported from scanner`, null);
    }

    // Clear after import so we don't re-import on next mount
    if (picks.length > 0) {
      try { localStorage.removeItem(BRIDGE_KEY); } catch {}
    }

    return added;
  }, [watchlist, addSymbol]);

  // Run once on mount — import any pending picks
  useEffect(() => {
    importPicks(); // async — runs in background
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally only on mount

  // Also poll every 10 seconds for new scanner picks while app is open
  useEffect(() => {
    const interval = setInterval(() => { importPicks(); }, 10_000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Also listen for storage events — so if scanner is open in another tab,
  // picks flow through automatically when the button is clicked
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === BRIDGE_KEY && e.newValue) {
        importPicks();
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [importPicks]);

  return { importPicks, getScannerContext };
}
